import { copyToClipboard } from "@/lib/js-helpers/copyToClipboard";
import { Check, Copy, LucideProps, X } from "lucide-react";
import { ComponentProps, Dispatch, SetStateAction, useState } from "react";
import { Button } from "./button";

interface CopyButtonProps
  extends Omit<ComponentProps<typeof Button>, "children" | "onClick"> {
  copyText: string;
  iconSize?: number;
}

type CopyState = "ready" | "success" | "fail";

export function CopyButton({
  copyText,
  iconSize = 16,
  size = "icon",
  ...props
}: CopyButtonProps) {
  const [copyState, setCopyState] = useState<CopyState>("ready");

  return (
    <Button
      {...props}
      size={size}
      onClick={() => handleCopyToClipboard(copyText, setCopyState)}
    >
      <CopyIcon state={copyState} size={iconSize} />
    </Button>
  );
}

async function handleCopyToClipboard(
  copyText: string,
  handleResolve: Dispatch<SetStateAction<CopyState>>
) {
  copyToClipboard(copyText)
    .then(() => handleResolve("success"))
    .catch(() => handleResolve("fail"));

  setTimeout(() => handleResolve("ready"), 3000);
}

interface CopyIconProps extends LucideProps {
  state: CopyState;
}

function CopyIcon({ state, ...props }: CopyIconProps) {
  switch (state) {
    case "success":
      return <Check {...props} data-testid="copySuccessIcon" />;
    case "fail":
      return (
        <X {...props} className="text-brand-error" data-testid="copyFailIcon" />
      );
    case "ready":
    default:
      return <Copy {...props} data-testid="copyReadyIcon" />;
  }
}
